import { useNavigate } from 'react-router-dom';
import {
  Chip,
  ErrorBanner,
  FAB,
  GroupCard,
  EmptyState,
  ListSkeleton,
  MainButton,
  Row,
  SectionHeader,
} from '../components';
import { useActiveList, useLists } from '../api/queries';
import { displayName } from '../api/format';
import { useSurface } from '../hooks/useSurface';

/**
 * The mobile overview: two sections (Yours / Shared with you) of list rows.
 * On desktop this route renders the detail pane instead — see `routes.tsx`.
 */
export function OverviewScreen() {
  const navigate = useNavigate();
  const { isTelegram, isWebMobile } = useSurface();
  const { data: lists, isPending, isError, error, refetch } = useLists();
  const { data: active } = useActiveList();

  const owned = (lists ?? []).filter((l) => l.role === 'owner');
  const shared = (lists ?? []).filter((l) => l.role !== 'owner');
  const isEmpty = !isPending && !isError && (lists?.length ?? 0) === 0;

  return (
    <>
      {isWebMobile ? (
        <header className="app-header">
          <div className="app-header__title">Lists</div>
          <div className="app-header__spacer" />
          <button
            type="button"
            className="app-header__icon-button"
            aria-label="Search"
            onClick={() => navigate('/search')}
          >
            ⌕
          </button>
        </header>
      ) : null}

      {isError ? <ErrorBanner message={error.message} onRetry={() => void refetch()} /> : null}

      <div className="screen-body screen-content">
        {isPending ? <ListSkeleton /> : null}

        {isEmpty ? (
          <EmptyState
            glyph="☰"
            title="No lists yet"
            body="You don't have any lists yet. Create one to get started."
            action={{ label: 'Create a list', onClick: () => navigate('/new') }}
          />
        ) : null}

        {!isPending && !isEmpty ? (
          <div style={{ padding: '8px 0 24px' }}>
            {owned.length > 0 ? (
              <>
                <SectionHeader>Yours</SectionHeader>
                <GroupCard>
                  {owned.map((list) => (
                    <Row
                      key={list.id}
                      title={list.name}
                      badge={
                        // The active list is shared with the bot, so it is
                        // worth showing which one an inline capture would hit.
                        active?.listId === list.id ? <Chip variant="muted">active</Chip> : undefined
                      }
                      subtitle={`${list.itemCount} items · ${list.doneCount} done`}
                      onClick={() => navigate(`/lists/${list.id}`)}
                    />
                  ))}
                </GroupCard>
              </>
            ) : null}

            {shared.length > 0 ? (
              <>
                <SectionHeader>Shared with you</SectionHeader>
                <GroupCard>
                  {shared.map((list) => (
                    <Row
                      key={list.id}
                      title={list.name}
                      badge={<Chip variant="badge">{list.role}</Chip>}
                      subtitle={`${displayName(list.ownerName, list.ownerId)} · ${list.itemCount} items · ${list.doneCount} done`}
                      onClick={() => navigate(`/lists/${list.id}`)}
                    />
                  ))}
                </GroupCard>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Telegram gets the native MainButton; web-mobile gets the FAB. Never both. */}
      {isTelegram ? (
        <MainButton label="New list" onClick={() => navigate('/new')} loading={isPending} />
      ) : null}
      {isWebMobile && !isPending && !isEmpty ? <FAB onClick={() => navigate('/new')} /> : null}
    </>
  );
}
